import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/ui/Modal";
import Button from "../../components/ui/Button";
import useProjectStore from "../../store/projectStore";
import { useLookups } from "../../hooks/useLookups";
import useCheerStore from "../../store/cheerStore";

const INITIAL = {
  name:         "",
  description:  "",
  topology:     "RoundEdge",
  meco_type_id: null,
  n_blades:     "",
};

const SELECT_CLS = `w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800
  focus:outline-none focus:ring-2 focus:ring-bh-400 bg-white`;

export default function NewDesignWizard({ open, onClose, projectId }) {
  const { createDesign } = useProjectStore();
  const { types: mecoTypes } = useLookups("meco_type");
  const triggerCheer = useCheerStore((s) => s.trigger);
  const navigate = useNavigate();

  const [form, setForm] = useState(INITIAL);
  const [creating, setCreating] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value || (k === "meco_type_id" ? null : "") }));

  const reset = () => setForm(INITIAL);
  const handleClose = () => { reset(); onClose(); };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = {
        name:        form.name,
        description: form.description,
        topology:    form.topology,
        project_id:  Number(projectId),
        ...(form.meco_type_id ? { meco_type_id: Number(form.meco_type_id) } : {}),
        ...(form.n_blades !== "" ? { n_blades: Number(form.n_blades) } : {}),
      };
      const d = await createDesign(payload);
      handleClose();
      navigate(`/projects/${projectId}/blade/${d.id}/summary`);
      triggerCheer("new_design");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="New Blade Design" size="md">
      <div className="space-y-5">

        {/* Name */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Design name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Design name"
            className={SELECT_CLS}
          />
        </div>

        {/* Topology + Component type side by side */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Topology <span className="text-red-400">*</span>
            </label>
            <select value={form.topology} onChange={set("topology")} className={SELECT_CLS}>
              <option value="RoundEdge">Round Edge</option>
              <option value="DoveTail">Dove Tail</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Component type{" "}
              <span className="font-normal normal-case text-gray-400">(optional)</span>
            </label>
            <select value={form.meco_type_id ?? ""} onChange={set("meco_type_id")} className={SELECT_CLS}>
              <option value="">— Not set —</option>
              {mecoTypes.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Number of blades */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            No. of blades{" "}
            <span className="font-normal normal-case text-gray-400">(optional)</span>
          </label>
          <input
            type="number"
            min="1"
            step="1"
            value={form.n_blades}
            onChange={set("n_blades")}
            placeholder="e.g. 5"
            className={SELECT_CLS}
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Description{" "}
            <span className="font-normal normal-case text-gray-400">(optional)</span>
          </label>
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            rows={2}
            placeholder="Short note about this design…"
            className={`${SELECT_CLS} resize-none`}
          />
        </div>

        <div className="flex justify-between pt-2 border-t border-gray-100">
          <Button variant="ghost" type="button" onClick={handleClose}>Cancel</Button>
          <Button
            type="button"
            onClick={handleCreate}
            loading={creating}
            disabled={!form.name.trim()}
          >
            Create design
          </Button>
        </div>
      </div>
    </Modal>
  );
}
